/** Represents a specific planning regime (morning, afternoon, evening) within a day for granular task scheduling. */
import { useDroppable } from '@dnd-kit/core';
import { Box } from '@mui/material';
import GridTaskItem from '@/features/planning/components/shared/GridTaskItem';
import GCalEventItem from '@/features/planning/components/shared/GCalEventItem';
import CapacityDot from '@/features/planning/components/shared/CapacityDot';
import { filterGCalEventsForTimeSlot } from '@/features/planning/utils/gcalHorizonUtils';
import { FIFTEEN_MINUTE_SLOT_HEIGHT } from '@/config/constants';

const filterTasksByTimeSlot = (tasks, timeSlot, regime) => {
  if (!tasks) return [];

  if (timeSlot === 'allday') {
    return tasks.filter((task) => !task.time || task.time === 'allday');
  }

  if (regime && regime.start && regime.end) {
    return tasks.filter(
      (task) =>
        task.time && task.time >= regime.start && task.time < regime.end,
    );
  }

  return [];
};

const TimeSlotCell = ({
  title,
  timeSlot,
  date,
  tasks,
  regime = null,
  gcalEvents = [],
  plannedMins = 0,
  maxMins = 0,
}) => {
  const displayTasks = filterTasksByTimeSlot(tasks, timeSlot, regime);
  const displayEvents = filterGCalEventsForTimeSlot(
    gcalEvents,
    timeSlot,
    regime,
  );
  const isAllday = timeSlot === 'allday';
  const droppableId = `${date.toISOString().split('T')[0]}_${timeSlot}`;

  const { isOver, setNodeRef } = useDroppable({
    id: droppableId,
    data: {
      type: 'timeslot',
      date: date.toISOString(),
      timeSlot,
    },
  });

  return (
    <Box
      sx={{
        height: '100%',
        minHeight: isAllday ? FIFTEEN_MINUTE_SLOT_HEIGHT : 0,
        border: 1,
        borderColor: 'divider',
        borderTop: 0,
        bgcolor: isAllday ? 'grey.50' : 'background.paper',
        p: isAllday ? '3px' : '6px',
        display: 'flex',
        flexDirection: 'column',
        boxSizing: 'border-box',
      }}
    >
      {title ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '4px',
            mb: '2px',
          }}
        >
          <Box
            sx={{
              fontWeight: 700,
              color: 'text.subtle',
              fontSize: '0.65em',
            }}
          >
            {title}
          </Box>
          <CapacityDot planned={plannedMins} max={maxMins} />
        </Box>
      ) : null}

      <Box
        ref={setNodeRef}
        sx={{
          flexGrow: 1,
          overflowY: 'auto',
          minHeight: 0,
          bgcolor: isOver ? 'action.hover' : 'transparent',
        }}
      >
        {displayTasks.map((task) => (
          <GridTaskItem key={task.id} task={task} />
        ))}
        {displayEvents.map((event) => (
          <GCalEventItem key={event.id} event={event} />
        ))}
      </Box>
    </Box>
  );
};

export default TimeSlotCell;
